import { useState } from "react";
import ModalComponent from "src/core/components/modal/modal.component";

type ZoneOption = {
  id: number;
  name: string;
};

type AddZoneModalProps = {
  showModal: boolean;
  handleClose: () => void;
  handleSave: (zoneName: string, assetId: number | null) => void;
  preselectedZoneId?: number | null;
  zoneOptions: ZoneOption[];
  isSubmitting?: boolean;
  fetchStatus?: "idle" | "pending" | "success" | "failed";
  fetchError?: string | null;
};

export function AddZoneModal({
  showModal,
  handleClose,
  handleSave,
  preselectedZoneId,
  zoneOptions,
  isSubmitting = false,
  fetchStatus = "idle",
  fetchError = null,
}: AddZoneModalProps) {
  const [zoneName, setZoneName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(
    preselectedZoneId ?? null
  );

  const filteredOptions = zoneOptions.filter((option) =>
    option.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleSaveClick = () => {
    handleSave(zoneName, selectedAssetId);
  };

  return (
    <ModalComponent
      showModal={showModal}
      handleClose={handleClose}
      headerTitle={
        <div className="add-zone-header">
          <div className="add-zone-header__left">
            <span className="add-zone-header__icon">
              <i className="bi bi-map" />
            </span>
            <div>
              <h3 className="add-zone-header__title">Add New Zone</h3>
              <p className="add-zone-header__subtitle">
                Zone {preselectedZoneId ?? ""}
              </p>
            </div>
          </div>
        </div>
      }
      actionConfig={[
        {
          label: "Save Zone",
          event: handleSaveClick,
          variant: "primary" as const,
          disabled: isSubmitting || !zoneName.trim(),
        },
      ]}
      size="lg"
    >
      <div className="add-zone-form">
        <div className="add-zone-form__row">
          <label className="add-zone-form__label">Zone Name</label>
          <input
            className="add-zone-form__input"
            type="text"
            placeholder="e.g. North Corridor Section B"
            value={zoneName}
            onChange={(event) => setZoneName(event.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <div className="add-zone-form__row">
          <label className="add-zone-form__label">Available Assets</label>
          <div className="add-zone-asset-section">
            <div className="add-zone-search">
              <span className="input-group-text">
                <i className="bi bi-search" />
              </span>
              <input
                className="form-control"
                type="text"
                placeholder="Search assets..."
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                disabled={isSubmitting || fetchStatus === "pending"}
              />
            </div>

            {fetchStatus === "pending" && (
              <div className="add-zone-asset-list__loading">
                Loading assets...
              </div>
            )}

            {fetchStatus !== "pending" && fetchError && (
              <div className="add-zone-asset-list__error">
                {fetchError}
              </div>
            )}

            {fetchStatus !== "pending" && !fetchError && (
              <div className="add-zone-asset-list">
                {filteredOptions.length === 0 && (
                  <div className="add-zone-asset-list__empty">
                    No assets found.
                  </div>
                )}
                {filteredOptions.map((option) => (
                  <div
                    key={option.id}
                    className={`add-zone-asset-item ${
                      selectedAssetId === option.id
                        ? "add-zone-asset-item--active"
                        : ""
                    }`}
                    onClick={() => setSelectedAssetId(option.id)}
                  >
                    {option.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalComponent>
  );
}
